import type {
  DocumentResponse,
  UpdateDocumentRequest,
  DocumentStatsResponse,
  SuggestionResponse,
} from "../types/api";

class APIError extends Error {
  statusCode: number;
  data?: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.data = data;
  }
}

const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return "/api";
};

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  const response = await fetch(url, config);
  let data: unknown;
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorMessage =
      typeof data === "object" && data !== null && "error" in data
        ? (data as { error: string }).error
        : `Request failed with status ${response.status}`;
    throw new APIError(errorMessage, response.status, data);
  }

  return data as T;
}

export const documentAPI = {
  getByProjectId: async (orgId: string, projectId: string): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse }>(
      `/organizations/${orgId}/projects/${projectId}/document`,
    );
    return response.document;
  },

  getById: async (orgId: string, documentId: string): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse }>(
      `/organizations/${orgId}/documents/${documentId}`,
    );
    return response.document;
  },

  update: async (
    orgId: string,
    documentId: string,
    data: UpdateDocumentRequest,
  ): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse }>(
      `/organizations/${orgId}/documents/${documentId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.document;
  },

  getStats: async (orgId: string, documentId: string): Promise<DocumentStatsResponse> => {
    const response = await fetchAPI<{ stats: DocumentStatsResponse }>(
      `/organizations/${orgId}/documents/${documentId}/stats`,
    );
    return response.stats;
  },

  generateDraft: async (orgId: string, projectId: string): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse; draft_text: string }>(
      `/organizations/${orgId}/projects/${projectId}/generate-draft`,
      { method: "POST" },
    );
    return response.document;
  },

  importFromStream: async (
    orgId: string,
    projectId: string,
    mode: "replace" | "append" = "replace",
  ): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse }>(
      `/organizations/${orgId}/projects/${projectId}/import-stream?mode=${mode}`,
      { method: "POST" },
    );
    return response.document;
  },

  importFromOutline: async (
    orgId: string,
    projectId: string,
    mode: "replace" | "append" = "replace",
  ): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse }>(
      `/organizations/${orgId}/projects/${projectId}/import-outline?mode=${mode}`,
      { method: "POST" },
    );
    return response.document;
  },
};

export const suggestionAPI = {
  getByDocumentId: async (orgId: string, documentId: string): Promise<SuggestionResponse[]> => {
    const response = await fetchAPI<{ suggestions: SuggestionResponse[] }>(
      `/organizations/${orgId}/documents/${documentId}/suggestions`,
    );
    return response.suggestions;
  },

  accept: async (
    orgId: string,
    documentId: string,
    suggestionId: string,
  ): Promise<SuggestionResponse> => {
    const response = await fetchAPI<{ suggestion: SuggestionResponse }>(
      `/organizations/${orgId}/documents/${documentId}/suggestions/${suggestionId}/accept`,
      {
        method: "POST",
      },
    );
    return response.suggestion;
  },

  reject: async (
    orgId: string,
    documentId: string,
    suggestionId: string,
  ): Promise<SuggestionResponse> => {
    const response = await fetchAPI<{ suggestion: SuggestionResponse }>(
      `/organizations/${orgId}/documents/${documentId}/suggestions/${suggestionId}/reject`,
      {
        method: "POST",
      },
    );
    return response.suggestion;
  },
};
